import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { TeamIcon } from "components/brandlibrary/MingleIcons";
import FilterAndPaging from "components/FilterAndPaging";
import { rowsPerpageOptions } from "components/Pagination";
import useTeams from "hooks/useTeams";
import { nanoid } from "nanoid";
import * as React from "react";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ILeague,
  IOrganization,
  ISearchTeamsParams,
  MemberRoleEnum,
} from "utils/api.interfaces";
import { StyledTableCell, StyledTableRowNoPointer } from "utils/common";
import ActionMenu from "../components/ActionMenu";
import ButtonOrganization from "../components/ButtonOrganization";
import ButtonOverviewDetailLink from "../components/ButtonOverviewDetailLink";
import OfficialBadge from "../components/OfficialBadge";
import FilterLeague from "../components/FilterLeague";
import FilterOrganization from "../components/FilterOrganization";
import { TeamUsersAmount } from "../components/TeamUsersAmount";
import DialogDeleteTeam from "../components/DialogDeleteTeam";
import { restartActiveTeamsStats } from "../hooks/useTeam";

export default function Teams() {
  const navigate = useNavigate();
  let [queryParams, setQueryParams] = useSearchParams();

  const initialSearchParams: ISearchTeamsParams = {
    page: queryParams.get("page") || "0",
    TeamNameFilter: queryParams.get("TeamNameFilter") || "",
    OrganizationIdFilter: queryParams.get("OrganizationIdFilter") || "",
    LeagueIdFilter: queryParams.get("LeagueIdFilter") || "",
    pageSize: queryParams.get("pageSize") || rowsPerpageOptions[2].toString(),
  };

  const [searchParams, setSearchParams] =
    useState<ISearchTeamsParams>(initialSearchParams);
  const { isLoading, dataTeams } = useTeams(searchParams);
  const [clickedTeamId, setClickedTeamId] = React.useState<string>("");
  const [showDeleteDialog, setShowDeleteDialog] =
    React.useState<boolean>(false);
  const [isRestartingStats, setIsRestartingStats] =
    React.useState<boolean>(false);

  const handleRestartActiveTeamsStats = async () => {
    if (
      !window.confirm(
        "Are you sure you want to restart stats for all active teams?"
      )
    )
      return;
    setIsRestartingStats(true);
    try {
      const result = await restartActiveTeamsStats();
      const message = [
        `Total teams: ${result.totalTeams}`,
        `Successful: ${result.successCount}`,
        `Failed: ${result.failedCount}`,
        ...(result.failedTeamIds.length > 0
          ? [`Failed team IDs:\n${result.failedTeamIds.join("\n")}`]
          : []),
      ].join("\n");
      alert(message);
    } catch {
      alert("Failed to restart stats");
    } finally {
      setIsRestartingStats(false);
    }
  };

  console.log("dataTeams", dataTeams);
  const handleParamsChange = (updatedParam: any) => {
    const newParams: any = {
      ...Object.fromEntries(new URLSearchParams(initialSearchParams)),
      ...updatedParam,
    };
    setSearchParams(newParams);
  };

  const handleTeamNameFilterChangeFilter = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleParamsChange({
      TeamNameFilter: event.target.value,
      page: "0",
      pageSize: rowsPerpageOptions[2].toString(),
    });
  };
  const handleOrganizationChange = (
    event: React.ChangeEvent<unknown>,
    organization: IOrganization
  ) => {
    organization
      ? handleParamsChange({
          OrganizationIdFilter: organization.id,
          page: "0",
          pageSize: rowsPerpageOptions[2].toString(),
        })
      : handleParamsChange({
          OrganizationIdFilter: "",
          page: "0",
          pageSize: rowsPerpageOptions[2].toString(),
        });
  };
  const handleLeagueChange = (
    event: React.ChangeEvent<unknown>,
    league: ILeague
  ) => {
    league
      ? handleParamsChange({
          LeagueIdFilter: league.id,
          page: "0",
          pageSize: rowsPerpageOptions[2].toString(),
        })
      : handleParamsChange({
          LeagueIdFilter: "",
          page: "0",
          pageSize: rowsPerpageOptions[2].toString(),
        });
  };

  React.useEffect(() => {
    setQueryParams(searchParams);
  }, [searchParams]);
  return (
    <>
      <Typography
        component="h1"
        variant="h1"
        color="inherit"
        noWrap
        gutterBottom
        sx={{
          textAlign: "center",
          margin: "40px 0px",
        }}
      >
        Teams list & search
      </Typography>
      {!isLoading ? (
        <>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              component={Link}
              size="small"
              variant="contained"
              color="success"
              to={`/teams/add/`}
              startIcon={<TeamIcon />}
            >
              add new team
            </Button>
            <Button
              size="small"
              variant="contained"
              style={{ marginLeft: "auto" }}
              color="warning"
              onClick={handleRestartActiveTeamsStats}
              disabled={isRestartingStats}
              startIcon={<RestartAltIcon />}
            >
              {isRestartingStats ? "Restarting..." : "Restart All Stats"}
            </Button>
          </Stack>

          <FilterAndPaging
            data={dataTeams}
            filterPagingParams={initialSearchParams}
            onParamsChange={handleParamsChange}
            showSearchField={false}
            filterOptions={
              <>
                <TextField
                  margin="dense"
                  id="filter"
                  sx={{
                    minWidth: "300px",
                  }}
                  label="Search team name"
                  variant="standard"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleTeamNameFilterChangeFilter(event);
                  }}
                  defaultValue={initialSearchParams.TeamNameFilter}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label="" edge="end">
                          {isLoading ? (
                            <CircularProgress size={15} />
                          ) : (
                            <FilterListIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FilterOrganization
                  onOrganizationChange={handleOrganizationChange}
                  initSelectedOrganization={searchParams.OrganizationIdFilter}
                />
                <FilterLeague
                  onLeagueChange={handleLeagueChange}
                  initSelectedLeague={searchParams.LeagueIdFilter}
                />
              </>
            }
            TableHeaders={
              <TableRow>
                <StyledTableCell>Team</StyledTableCell>
                <StyledTableCell>Members</StyledTableCell>
                <StyledTableCell>Players</StyledTableCell>
                <StyledTableCell>Trainers</StyledTableCell>
                <StyledTableCell>Supporters</StyledTableCell>
                <StyledTableCell align="right">Organization</StyledTableCell>
                <StyledTableCell align="right">Action</StyledTableCell>
              </TableRow>
            }
          >
            {dataTeams?.teams?.map((team) => (
              <StyledTableRowNoPointer key={nanoid(6)} hover>
                <StyledTableCell>
                  <ButtonOverviewDetailLink
                    link={`/teams/${team.id}`}
                    title={team.name ? team.name : "no known name"}
                  />

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">
                      {team.name ? team.name : "no known name"}
                    </Typography>
                    {team.isOfficial && <OfficialBadge />}
                  </Stack>
                  <Typography variant="body2">
                    Invitecode: {team.inviteCode}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {team.members.length}
                  </Typography>
                </StyledTableCell>
                <TeamUsersAmount
                  players={team?.members?.filter((member) =>
                    member.rolesData.some(
                      (role) => role.roleType === MemberRoleEnum.player
                    )
                  )}
                  trainers={team?.members?.filter((member) =>
                    member.rolesData.some(
                      (role) =>
                        role.roleType === MemberRoleEnum.trainer ||
                        role.roleType === MemberRoleEnum.assistant ||
                        role.roleType === MemberRoleEnum.manager ||
                        role.roleType === MemberRoleEnum.coach
                    )
                  )}
                  support={team?.members?.filter((member) =>
                    member.rolesData.some(
                      (role) =>
                        role.roleType === MemberRoleEnum.support ||
                        role.roleType === MemberRoleEnum.parent
                    )
                  )}
                />

                <StyledTableCell align="right">
                  <ButtonOrganization organizationId={team?.organization?.id} />
                </StyledTableCell>
                <StyledTableCell align="right">
                  <ActionMenu
                    itemId={team?.id}
                    handleNewClick={(e) => {
                      navigate(`/teams/add/`);
                    }}
                    handleEditClick={(e, id) => {
                      navigate(`/teams/edit/${id}`);
                    }}
                    showDeleteButton={true}
                    handleDeleteClick={(e, id) => {
                      if (id) {
                        setClickedTeamId(id);
                        setShowDeleteDialog(true);
                      }
                    }}
                  />
                </StyledTableCell>
              </StyledTableRowNoPointer>
            ))}
          </FilterAndPaging>
        </>
      ) : (
        <Box sx={{ marginTop: "70px" }}>
          {Array(Number(initialSearchParams.pageSize))
            .fill(1)
            .map((el, i) => (
              <Skeleton animation="wave" height={60} key={nanoid(6)} />
            ))}
        </Box>
      )}
      <DialogDeleteTeam
        id={clickedTeamId}
        isOpen={showDeleteDialog}
        onIsOpenChange={setShowDeleteDialog}
      />
    </>
  );
}
